/**
 * If the row is already saved (it came from the backend), we send the selected filterItemReferences
 * with the request so the backend can check that those activities still exist on the latest version.
 */
import { useMemo, useRef } from 'react';

import { FilterReferenceKey } from 'api/queries/getFilterItemOptionsByRowType.query';
import { FilterItemReference } from 'api/queries/getPoapViewById.query';
import { RowTypeKey } from 'api/queries/getRowTypeOptions.query';

import type { FormValues } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfig/RowConfig';

export type SavedRefsBaseline = {
    rowType: RowTypeKey;
    filterItem: FilterReferenceKey;
    projectId: number;
    contractId: number;
    filterItemReferencesSnapshot: FilterItemReference[];
};

function cloneFilterRefs(refs: FilterItemReference[] | undefined): FilterItemReference[] {
    return (refs ?? []).map((r) => ({ ...r }));
}

function useSavedRefsBaseline(
    rowIndex: number,
    rowId: number | undefined,
    rowType: RowTypeKey | undefined,
    filterItem: FilterReferenceKey | undefined,
    projectId: number | undefined,
    contractId: number | undefined,
    filterItemReferences: FilterItemReference[] | undefined,
): SavedRefsBaseline | null {
    const indexRef = useRef(rowIndex);
    const baselineRef = useRef<SavedRefsBaseline | null>(null);

    if (indexRef.current !== rowIndex) {
        indexRef.current = rowIndex;
        baselineRef.current = null;
    }

    if (rowId == null) {
        baselineRef.current = null;
    } else if (
        baselineRef.current == null &&
        rowType != null &&
        filterItem != null &&
        projectId != null &&
        contractId != null
    ) {
        baselineRef.current = {
            rowType,
            filterItem,
            projectId,
            contractId,
            filterItemReferencesSnapshot: cloneFilterRefs(filterItemReferences),
        };
    } else if (baselineRef.current != null) {
        const b = baselineRef.current;
        const contextMatches =
            rowType === b.rowType &&
            filterItem === b.filterItem &&
            projectId === b.projectId &&
            contractId === b.contractId;
        const refsArrivedLate =
            contextMatches && b.filterItemReferencesSnapshot.length === 0 && (filterItemReferences?.length ?? 0) > 0;
        if (refsArrivedLate) {
            baselineRef.current = {
                ...b,
                filterItemReferencesSnapshot: cloneFilterRefs(filterItemReferences),
            };
        }
    }

    return baselineRef.current;
}

export interface FilterItemReferencesSnapshotInput {
    rowIndex: number;
    row: FormValues;
}

export function useFilterItemReferencesSnapshot({
    rowIndex,
    row,
}: FilterItemReferencesSnapshotInput): { label: string; value: string }[] {
    const rowId = row.rowId;
    const rowType = row.rowType;
    const filterItem = row.filterItem;
    const projectId = row.project?.id;
    const contractId = row.contract?.id;
    const filterItemReferences = row.filterItemReferences;

    const savedRefsBaseline = useSavedRefsBaseline(
        rowIndex,
        rowId,
        rowType,
        filterItem,
        projectId,
        contractId,
        filterItemReferences,
    );

    return useMemo(() => {
        const contextUnchanged =
            rowId != null &&
            savedRefsBaseline != null &&
            rowType === savedRefsBaseline.rowType &&
            filterItem === savedRefsBaseline.filterItem &&
            projectId === savedRefsBaseline.projectId &&
            contractId === savedRefsBaseline.contractId;
        if (!contextUnchanged) return [];
        return savedRefsBaseline.filterItemReferencesSnapshot.map((r) => ({
            value: r.value,
            label: r.label,
        }));
    }, [rowId, savedRefsBaseline, rowType, filterItem, projectId, contractId]);
}
